import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Alert, Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem, Stack,
  TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AccountTree as SitesIcon,
  LocalHospital as HospitalIcon,
  MedicalServices as ClinicIcon,
  Edit as EditIcon,
  ToggleOn as ActiveIcon,
  ToggleOff as InactiveIcon,
  Login as EnterIcon,
} from '@mui/icons-material';
import axios from 'axios';
import * as orgApi from '../api/organization';
import { useAuth } from '../contexts/AuthContext';
import StructureTree from '../components/StructureTree';
import { announceSitesChanged } from '../utils/sitesChanged';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const EMPTY_FORM = {
  name: '', type: 'clinic', address: '', phone: '', email: '',
  admin_email: '', admin_full_name: '',
};

/**
 * Every clinic/hospital in the organisation, and the button that opens a new one.
 * This is the screen the multi-site feature exists for.
 */
export default function OrgSites() {
  const { user } = useAuth();
  // The site this session is already inside. An org admin holds no clinic, so this is
  // null for them and every card keeps its Open button, which is correct — they are not
  // "in" any of these sites.
  const currentClinicId = user?.clinic_id || null;
  const [data, setData] = useState({ organization: null, sites: [] });
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [entering, setEntering] = useState(null);
  // An organisation admin also gets the organisation's structure tree, with Open on each
  // region and office. A top-level site's clinic admin manages sites, not regions.
  const isOrgAdmin = user?.role === 'org_admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await orgApi.listSites());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load your sites');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitNew = async () => {
    if (!form.name.trim()) { toast.error('Give the site a name'); return; }
    setSaving(true);
    try {
      const created = await orgApi.createSite({
        name: form.name.trim(),
        type: form.type,
        address: form.address.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        admin_email: form.admin_email.trim() || null,
        admin_full_name: form.admin_full_name.trim() || null,
      });
      toast.success(
        created.admin_invite_status === 'invited'
          ? `${created.name} created — invitation sent to its administrator`
          : `${created.name} created`,
      );
      setAddOpen(false);
      setForm(EMPTY_FORM);
      load();
      announceSitesChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create the site');
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async () => {
    setSaving(true);
    try {
      await orgApi.updateSite(editing.id, {
        name: form.name.trim(),
        type: form.type,
        address: form.address.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
      });
      toast.success('Site updated');
      announceSitesChanged();
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update the site');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Go and work inside one of your sites.
   *
   * An organisation session names no clinic, so every clinic-scoped screen would have
   * nothing to load — which is why they are gated off for an org admin. Entering a site
   * swaps the session for a clinic_admin one at that clinic, which is the role
   * `can_access` already grants an org admin everywhere in their organisation. Uses the
   * ordinary portal handoff rather than a bespoke route, so the switch is authorised and
   * audited exactly like any other.
   */
  const enterSite = async (site) => {
    setEntering(site.id);
    try {
      const { data } = await axios.post(`${API_URL}/api/v1/auth/switch-portal`, {
        role: 'clinic_admin', clinic_id: site.id,
      });
      window.location.assign(data.handoff_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not open ${site.name}`);
      setEntering(null);
    }
  };

  const toggle = async (site) => {
    setBusyId(site.id);
    try {
      await orgApi.setSiteStatus(site.id, !site.is_active);
      toast.success(site.is_active ? `${site.name} deactivated` : `${site.name} reactivated`);
      announceSitesChanged();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change that site');
    } finally {
      setBusyId(null);
    }
  };

  const openEdit = (site) => {
    setForm({
      name: site.name || '', type: site.type || 'clinic',
      address: site.address || '', phone: site.phone || '', email: site.email || '',
      admin_email: '', admin_full_name: '',
    });
    setEditing(site);
  };

  const org = data.organization;
  const sites = data.sites || [];
  const activeCount = sites.filter((s) => s.is_active).length;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}><SitesIcon /></Avatar>
        <Box>
          <Typography variant="h4" fontWeight={700}>Sites</Typography>
          <Typography variant="body2" color="text.secondary">
            The clinics and hospitals that make up {org?.name || 'your organisation'}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ ml: 'auto' }}
          onClick={() => { setForm(EMPTY_FORM); setAddOpen(true); }}>
          Add site
        </Button>
      </Box>

      {!loading && sites.length === 1 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have one site. Adding another lets you share providers between them and
          manage both from here — each site keeps its own patients and records.
        </Alert>
      )}

      {/* An organisation admin sees the whole organisation as a tree — regions, the
          offices in each, sessions left — with Open on every row. A top-level site's
          clinic admin manages sites only, so they keep just the cards. */}
      {isOrgAdmin && <StructureTree />}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={2}>
          {sites.map((s) => (
            <Grid item xs={12} md={6} lg={4} key={s.id}>
              <Card sx={{ height: '100%', opacity: s.is_active ? 1 : 0.6 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                    <Avatar sx={{ bgcolor: s.type === 'hospital' ? 'secondary.main' : 'primary.main' }}>
                      {s.type === 'hospital' ? <HospitalIcon /> : <ClinicIcon />}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="h6" fontWeight={700} noWrap>{s.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.type === 'hospital' ? 'Hospital' : 'Clinic'}
                      </Typography>
                      {/* Which practice the others hang off. Without it a three-site
                          customer reads as three peers. */}
                      {s.is_region_practice && (
                        <Chip size="small" color="info" variant="outlined" label="Region practice"
                          sx={{ ml: 1, height: 20, fontSize: 11 }} />
                      )}
                      {s.is_primary_site && (
                        <Chip size="small" color="primary" variant="outlined" label="Main site"
                          sx={{ mt: 0.5, height: 20, fontSize: 11 }} />
                      )}
                    </Box>
                    {!s.is_active && <Chip label="Inactive" size="small" color="default" />}
                  </Box>

                  {s.address && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {s.address}
                    </Typography>
                  )}

                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                    <Chip size="small" label={`${s.counts.providers} providers`} />
                    <Chip size="small" label={`${s.counts.staff} staff`} />
                    <Chip size="small" label={`${s.counts.admins} admins`} />
                    <Chip size="small" label={`${s.counts.patients} patients`} />
                  </Stack>

                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {/* Offering "Open" on the site you are already working in was an
                        action that did nothing but hand you back the session you had.
                        It reads as your location instead. */}
                    {s.id === currentClinicId ? (
                      <Chip size="small" color="success" variant="outlined"
                        label="You are here" sx={{ mr: 0.5 }} />
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EnterIcon />}
                        disabled={!s.is_active || entering === s.id}
                        onClick={() => enterSite(s)}
                        sx={{ mr: 0.5 }}
                      >
                        {entering === s.id ? 'Opening…' : 'Open'}
                      </Button>
                    )}
                    <Tooltip title="Edit details">
                      <IconButton size="small" onClick={() => openEdit(s)}><EditIcon /></IconButton>
                    </Tooltip>
                    <Tooltip title={
                      s.is_active
                        ? (activeCount === 1
                            ? 'This is your only active site'
                            : 'Deactivate this site')
                        : 'Reactivate this site'
                    }>
                      <span>
                        <IconButton
                          size="small"
                          disabled={busyId === s.id || (s.is_active && activeCount === 1)}
                          onClick={() => toggle(s)}
                        >
                          {s.is_active ? <ActiveIcon color="success" /> : <InactiveIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add ------------------------------------------------------------- */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add a site</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" required fullWidth value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <TextField label="Type" select fullWidth value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}>
              <MenuItem value="clinic">Clinic</MenuItem>
              <MenuItem value="hospital">Hospital</MenuItem>
            </TextField>
            <TextField label="Address" fullWidth value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })} />
            <TextField label="Phone" fullWidth value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <TextField label="Contact email" fullWidth value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Alert severity="info">
              Optionally invite someone to administer this site. They get the usual
              single-use link and set their own password.
            </Alert>
            <TextField label="Site administrator email" fullWidth value={form.admin_email}
              onChange={(e) => setForm({ ...form, admin_email: e.target.value })} />
            <TextField label="Site administrator name" fullWidth value={form.admin_full_name}
              onChange={(e) => setForm({ ...form, admin_full_name: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitNew} disabled={saving}>
            {saving ? 'Creating…' : 'Create site'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit ------------------------------------------------------------ */}
      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit {editing?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" required fullWidth value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <TextField label="Type" select fullWidth value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}>
              <MenuItem value="clinic">Clinic</MenuItem>
              <MenuItem value="hospital">Hospital</MenuItem>
            </TextField>
            <TextField label="Address" fullWidth value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })} />
            <TextField label="Phone" fullWidth value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <TextField label="Contact email" fullWidth value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitEdit} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
